import { useState, useRef, useEffect } from "react";
import { Bell } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  useUnreadCount,
  useNotificationList,
  useMarkRead,
  useMarkAllRead,
  useRefreshFeedOnNewNotification,
} from "@/hooks/useNotifications";
import type { Notification } from "@/lib/services/community";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const NOTIFICATION_ICONS: Record<string, string> = {
  report_resolved: "",
  submission_approved: "",
  submission_rejected: "",
  feedback_acknowledged: "",
  feedback_resolved: "",
  comment_on_doc: "",
  comment_vote: "",
  new_follower: "",
  comment_on_post: "",
  reply_to_comment: "",
  post_upvote: "",
  post_comment_upvote: "",
  mention_in_post: "",
  mention_in_comment: "",
};

function getNotificationUrl(n: Notification): string | null {
  if (!n.related_type || !n.related_id) return null;
  if (n.related_type === "document") return `/docs?doc=${n.related_id}`;
  if (n.related_type === "post") return `/feed/${n.related_id}`;
  return null;
}

function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onNavigate: () => void;
}) {
  const icon = NOTIFICATION_ICONS[notification.type] ?? "🔔";
  const ago = timeAgo(notification.created_at);
  const url = getNotificationUrl(notification);

  function handleClick() {
    if (!notification.is_read) onMarkRead(notification.id);
    if (url) onNavigate();
  }

  return (
    <div
      onClick={url ? handleClick : undefined}
      className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
        !notification.is_read ? "bg-[#CA8A04]/5" : ""
      } ${url ? "cursor-pointer" : ""}`}
    >
      <span className="text-base mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-snug ${
            !notification.is_read
              ? "font-semibold text-gray-900"
              : "font-medium text-gray-700"
          }`}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            {notification.body}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1">{ago}</p>
      </div>
      {!notification.is_read && (
        <button
          onClick={() => onMarkRead(notification.id)}
          className="shrink-0 w-2 h-2 rounded-full bg-[#CA8A04] mt-2 hover:bg-[#B8860B] transition-colors"
          title="Mark as read"
        />
      )}
    </div>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notifications = [], isLoading } = useNotificationList(open);
  const { mutate: markRead } = useMarkRead();
  const { mutate: markAllRead, isPending: markingAll } = useMarkAllRead();

  // Live-refresh the feed's vote/comment counts when a new notification arrives.
  useRefreshFeedOnNewNotification();

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const displayCount = unreadCount > 9 ? "9+" : unreadCount;

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5 transition-all duration-300"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {displayCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-[60] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                disabled={markingAll}
                className="text-xs text-[#CA8A04] font-medium hover:text-[#B8860B] transition-colors disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-[#CA8A04] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No notifications yet</p>
              </div>
            ) : (
              notifications
                .slice(0, 8)
                .map((n) => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    onMarkRead={markRead}
                    onNavigate={() => {
                      const url = getNotificationUrl(n);
                      if (url) { setOpen(false); navigate(url); }
                    }}
                  />
                ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-3">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-[#CA8A04] font-medium hover:text-[#B8860B] transition-colors"
              >
                View all notifications →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
